/** Human model output only; provider envelopes and tool arguments stay out of chat. */
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const list = (value) => (Array.isArray(value) ? value : []);
const string = (value) => (typeof value === 'string' ? value : '');

function collector() {
  const parts = [];
  return {
    parts,
    append(kind, text, key) {
      if (!text) return;
      const last = parts[parts.length - 1];
      if (last?.kind === kind && last.key === key) last.text += text;
      else parts.push({ kind, text, key });
    },
    snapshot(kind, text, key) {
      if (!text) return;
      const matches = parts.filter((part) => part.kind === kind && part.key === key);
      const received = matches.map((part) => part.text).join('');
      // Done events and the completed response repeat already received deltas.
      if (matches.length && received.startsWith(text)) return;
      if (matches.length && text.startsWith(received)) {
        matches[matches.length - 1].text += text.slice(received.length);
        return;
      }
      this.append(kind, text, key);
    },
  };
}

function blockText(block) {
  return string(block.text) || (object(block.text) ? string(block.text.value) : '');
}

function blocks(content, target, prefix, snapshot = true) {
  if (typeof content === 'string') {
    target[snapshot ? 'snapshot' : 'append']('text', content, `${prefix}:text:0`);
    return;
  }
  list(content).forEach((block, index) => {
    if (!object(block)) return;
    const add = (kind, text) => target[snapshot ? 'snapshot' : 'append'](kind, text, `${prefix}:${kind}:${index}`);
    if (block.type === 'thinking') add('reasoning', string(block.thinking));
    else if (['text', 'output_text', 'refusal'].includes(block.type)) add('text', blockText(block) || string(block.refusal));
    else if (['summary_text', 'reasoning_text'].includes(block.type)) add('reasoning', blockText(block));
    else if (block.type === 'openai_item' && object(block.item)) item(block.item, target, `${prefix}:item:${index}`);
    else if (block.type === 'reasoning' || block.type === 'message') item(block, target, `${prefix}:item:${index}`);
  });
}

function item(value, target, prefix) {
  if (!object(value)) return;
  if (value.type === 'reasoning') {
    list(value.summary).forEach((part, index) => {
      if (object(part) && part.type === 'summary_text') target.snapshot('reasoning', blockText(part), `${prefix}:reasoning:${index}`);
    });
    list(value.content).forEach((part, index) => {
      if (object(part) && ['reasoning_text', 'text'].includes(part.type)) target.snapshot('reasoning', blockText(part), `${prefix}:reasoning-text:${index}`);
    });
  } else if (value.type === 'message' || (!value.type && Array.isArray(value.content))) {
    if (value.role && value.role !== 'assistant') return;
    blocks(value.content, target, prefix);
  }
}

function reasoningFields(value) {
  // Some compatible providers emit both aliases with the same value.
  return [...new Set([string(value.reasoning_content), string(value.reasoning)].filter(Boolean))];
}

function chat(chunks, target) {
  list(chunks).forEach((chunk) => {
    if (!object(chunk)) return;
    list(chunk.choices).forEach((choice) => {
      if (!object(choice) || (choice.index !== undefined && choice.index !== 0)) return;
      const delta = object(choice.delta) ? choice.delta : null;
      const value = delta || (object(choice.message) ? choice.message : null);
      if (!value) return;
      const write = delta ? 'append' : 'snapshot';
      reasoningFields(value).forEach((text) => target[write]('reasoning', text, 'chat:reasoning'));
      if (typeof value.content === 'string') target[write]('text', value.content, 'chat:text');
      else blocks(value.content, target, 'chat', !delta);
      if (typeof value.refusal === 'string') target[write]('text', value.refusal, 'chat:refusal');
    });
  });
}

function responses(events, target) {
  const ids = new Map();
  const indices = new Map();
  const itemKey = (id, index) => {
    const key = ids.get(id) || indices.get(index) || (Number.isInteger(index) ? `output:${index}` : `item:${string(id) || 'unknown'}`);
    if (typeof id === 'string' && id) ids.set(id, key);
    if (Number.isInteger(index)) indices.set(index, key);
    return key;
  };
  const output = (value) => {
    list(value?.output).forEach((part, index) => {
      if (object(part)) item(part, target, itemKey(part.id, index));
    });
    if (!list(value?.output).length) target.snapshot('text', string(value?.output_text), 'output:text');
  };
  list(events).forEach((event) => {
    if (!object(event)) return;
    const type = string(event.type);
    const prefix = itemKey(event.item_id || event.item?.id, event.output_index);
    const index = Number.isInteger(event.summary_index) ? event.summary_index : Number.isInteger(event.content_index) ? event.content_index : 0;
    const kind = type.includes('reasoning') ? 'reasoning' : 'text';
    const channel = type.includes('reasoning_text') ? 'reasoning-text' : kind;
    const key = `${prefix}:${channel}:${index}`;
    if (['response.output_text.delta', 'response.reasoning_summary_text.delta', 'response.reasoning_text.delta', 'response.refusal.delta'].includes(type)) {
      target.append(kind, string(event.delta), key);
    } else if (['response.output_text.done', 'response.reasoning_summary_text.done', 'response.reasoning_text.done', 'response.refusal.done'].includes(type)) {
      target.snapshot(kind, string(event.text) || string(event.refusal), key);
    } else if (type === 'response.output_item.added' || type === 'response.output_item.done') {
      item(event.item, target, prefix);
    } else if (['response.content_part.added', 'response.content_part.done', 'response.reasoning_summary_part.added', 'response.reasoning_summary_part.done'].includes(type)) {
      const part = event.part;
      if (!object(part)) return;
      if (['output_text', 'text', 'refusal'].includes(part.type)) target.snapshot('text', blockText(part) || string(part.refusal), `${prefix}:text:${index}`);
      else if (part.type === 'summary_text') target.snapshot('reasoning', blockText(part), `${prefix}:reasoning:${index}`);
    } else if (object(event.response)) output(event.response);
  });
}

function envelope(value, target) {
  if (!object(value)) return;
  if (Array.isArray(value.chunks)) {
    chat(value.chunks, target);
    if (target.parts.length) return;
  }
  if (Array.isArray(value.events)) {
    responses(value.events, target);
    if (target.parts.length) return;
  }
  if (Array.isArray(value.output)) {
    value.output.forEach((part, index) => item(part, target, `output:${index}`));
    if (target.parts.length) return;
  }
  if (Array.isArray(value.choices)) {
    chat([value], target);
    if (target.parts.length) return;
  }
  reasoningFields(value).forEach((text) => target.snapshot('reasoning', text, 'envelope:reasoning'));
  blocks(value.content, target, 'content');
  if (!target.parts.some((part) => part.kind === 'text')) target.snapshot('text', string(value.output_text) || string(value.text), 'envelope:text');
  if (!target.parts.length && object(value.response)) envelope(value.response, target);
}

function humanParts(parts) {
  const result = [];
  const add = (kind, text) => {
    if (!text) return;
    const last = result[result.length - 1];
    if (last?.kind === kind) last.text += text;
    else result.push({ kind, text });
  };
  // First join consecutive deltas so a vendor's opening <think> tag may span chunks.
  const joined = [];
  parts.forEach(({ kind, text }) => {
    const last = joined[joined.length - 1];
    if (last?.kind === kind) last.text += text;
    else joined.push({ kind, text });
  });
  let answering = false;
  joined.forEach(({ kind, text }) => {
    if (kind !== 'text' || answering) {
      add(kind, text);
      return;
    }
    let rest = text;
    while (rest) {
      const opening = rest.match(/^(\s*)<think>/);
      if (!opening) {
        add('text', rest);
        if (rest.trim()) answering = true;
        return;
      }
      const start = opening[0].length;
      const close = rest.indexOf('</think>', start);
      if (close < 0) {
        add('reasoning', opening[1] + rest.slice(start));
        return;
      }
      add('reasoning', opening[1] + rest.slice(start, close));
      rest = rest.slice(close + '</think>'.length);
    }
  });
  return result;
}

/**
 * One diagnostic {kind: 'model', response: ...} → ordered readable blocks.
 * Raw received streams determine order; normalized content/text fills missing
 * kinds only. Snapshots/envelopes do not duplicate their own streamed text.
 */
export function modelTranscript(entry) {
  if (!object(entry) || entry.kind !== 'model' || !object(entry.response)) return [];
  const raw = collector();
  envelope(entry.response.provider_response, raw);
  const normalized = collector();
  envelope(entry.response, normalized);
  const received = humanParts(raw.parts);
  if (!received.length) return humanParts(normalized.parts);
  const fallback = humanParts(normalized.parts).filter((part) => !received.some((current) => current.kind === part.kind));
  return humanParts([
    ...fallback.filter((part) => part.kind === 'reasoning'),
    ...received,
    ...fallback.filter((part) => part.kind === 'text'),
  ]);
}
